import React, { useEffect, useState } from "react";
import { ShieldCheck, Smartphone, Check, X } from "lucide-react";
import { Page, Card, Pill, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function MFASetup() {
  const { user } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const begin = async () => {
    setEnrolling(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
    } catch {
      toast.error("Could not start MFA setup");
    }
    setEnrolling(false);
  };

  const verify = async () => {
    if (!code) return;
    setVerifying(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("MFA enabled");
      // Update cached user
      const cached = JSON.parse(localStorage.getItem("mfp_user") || "{}");
      cached.mfa_enabled = true;
      localStorage.setItem("mfp_user", JSON.stringify(cached));
      setSetupData(null);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    }
    setVerifying(false);
  };

  const disable = async () => {
    const code = window.prompt("Enter your current 6-digit TOTP code to disable MFA:");
    if (!code) return;
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("MFA disabled");
      const cached = JSON.parse(localStorage.getItem("mfp_user") || "{}");
      cached.mfa_enabled = false;
      localStorage.setItem("mfp_user", JSON.stringify(cached));
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Page title="Two-Factor Authentication" subtitle="HIPAA security best practice. Required for high-risk roles.">
      <Card delay={0.05} testid="mfa-status-card" className="max-w-2xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: user?.mfa_enabled ? "rgba(123,174,127,0.18)" : "rgba(201,168,76,0.18)" }}>
            <ShieldCheck className="w-6 h-6" style={{ color: user?.mfa_enabled ? "#4a7a4e" : "#8a6f20" }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="font-serif text-2xl">Two-Factor Authentication</div>
              <Pill variant={user?.mfa_enabled ? "success" : "warn"}>{user?.mfa_enabled ? "Enabled" : "Not Enabled"}</Pill>
            </div>
            <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>
              An authenticator app generates time-based codes. SMS is intentionally not supported (SIM-swap risk).
            </div>
          </div>
        </div>

        {!user?.mfa_enabled && !setupData && (
          <div>
            <div className="space-y-2 mb-5 text-sm" style={{ color: "var(--mf-text-muted)" }}>
              <div className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> Compatible with Google Authenticator, Authy, 1Password, Bitwarden</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> Codes refresh every 30 seconds</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> Stored locally on your device — never transmitted</div>
            </div>
            <PrimaryButton onClick={begin} disabled={enrolling} testid="mfa-begin-btn">
              {enrolling ? "Generating QR…" : "Enable Two-Factor Authentication"}
            </PrimaryButton>
          </div>
        )}

        {setupData && (
          <div className="space-y-4" data-testid="mfa-qr-step">
            <div className="text-sm font-medium">Step 1 — Scan this QR code with your authenticator app:</div>
            <div className="bg-white p-5 rounded-2xl inline-block">
              <img src={`data:image/png;base64,${setupData.qr_code_base64}`} alt="MFA QR Code" className="w-48 h-48" data-testid="mfa-qr-image" />
            </div>
            <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>
              Or enter manually: <code className="px-2 py-1 rounded font-mono" style={{ background: "var(--mf-muted)" }}>{setupData.secret}</code>
            </div>
            <div className="text-sm font-medium pt-2">Step 2 — Enter the 6-digit code from your app:</div>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="w-40 text-center px-4 py-3 rounded-xl outline-none text-2xl font-mono tracking-widest"
              style={{ background: "var(--mf-muted)" }}
              data-testid="mfa-code-input"
              autoFocus
            />
            <div className="flex gap-2">
              <PrimaryButton onClick={verify} disabled={code.length !== 6 || verifying} testid="mfa-verify-btn">
                {verifying ? "Verifying…" : "Verify & Enable"}
              </PrimaryButton>
              <button onClick={() => setSetupData(null)} className="px-4 py-2 text-sm rounded-full" data-testid="mfa-cancel-btn">Cancel</button>
            </div>
          </div>
        )}

        {user?.mfa_enabled && (
          <button onClick={disable} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: "rgba(180,82,83,0.12)", color: "#b91c1c" }} data-testid="mfa-disable-btn">
            Disable MFA
          </button>
        )}
      </Card>
    </Page>
  );
}
